import tinycolor from "tinycolor2";

interface ColorGenerationOptions {
  preserveHue?: boolean;
  minContrast?: number;
}

/**
 * Generates a dark mode version of a color by intelligently adjusting brightness
 * while maintaining the color's character and ensuring good contrast
 */
export function generateDarkModeColor(
  lightColor: string,
  options: ColorGenerationOptions = {},
): string {
  const { preserveHue = true } = options;

  const color = tinycolor(lightColor);
  if (!color.isValid()) {
    return lightColor;
  }

  const hsl = color.toHsl();

  // Special cases for grayscale colors
  if (hsl.s < 0.1) {
    // Pure white or very light gray -> dark gray/black
    if (hsl.l > 0.9) {
      return tinycolor({ h: hsl.h, s: 0, l: 0.08 }).toHexString();
    }
    // Pure black or very dark gray -> light gray/white
    if (hsl.l < 0.1) {
      return tinycolor({ h: hsl.h, s: 0, l: 0.92 }).toHexString();
    }
    // Mid grays -> invert lightness
    return tinycolor({ h: hsl.h, s: hsl.s, l: 1 - hsl.l }).toHexString();
  }

  // For colored elements
  if (preserveHue) {
    // Light colors -> darken while increasing saturation slightly
    if (hsl.l > 0.6) {
      const newLightness = 0.15 + 0.25 * (1 - hsl.l);
      const newSaturation = Math.min(hsl.s * 1.2, 1);
      return tinycolor({
        h: hsl.h,
        s: newSaturation,
        l: newLightness,
      }).toHexString();
    }
    // Dark colors -> lighten
    if (hsl.l < 0.4) {
      const newLightness = 0.65 + 0.25 * hsl.l;
      return tinycolor({
        h: hsl.h,
        s: hsl.s * 0.9,
        l: newLightness,
      }).toHexString();
    }
    // Mid-range colors -> adjust based on saturation

    const targetLightness = hsl.s > 0.5 ? 0.3 : 0.7;
    return tinycolor({
      h: hsl.h,
      s: hsl.s,
      l: targetLightness,
    }).toHexString();
  }

  // Simple inversion (not recommended for most cases)
  return tinycolor({ h: hsl.h, s: hsl.s, l: 1 - hsl.l }).toHexString();
}

/**
 * Generates a light mode version of a color by intelligently adjusting brightness
 * while maintaining the color's character and ensuring good contrast
 */
export function generateLightModeColor(
  darkColor: string,
  options: ColorGenerationOptions = {},
): string {
  const { preserveHue = true } = options;

  const color = tinycolor(darkColor);
  if (!color.isValid()) {
    return darkColor;
  }

  const hsl = color.toHsl();

  // Special cases for grayscale colors
  if (hsl.s < 0.1) {
    // Pure black or very dark gray -> light gray/white
    if (hsl.l < 0.1) {
      return tinycolor({ h: hsl.h, s: 0, l: 0.92 }).toHexString();
    }
    // Pure white or very light gray -> dark gray/black
    if (hsl.l > 0.9) {
      return tinycolor({ h: hsl.h, s: 0, l: 0.08 }).toHexString();
    }
    // Mid grays -> invert lightness
    return tinycolor({ h: hsl.h, s: hsl.s, l: 1 - hsl.l }).toHexString();
  }

  // For colored elements
  if (preserveHue) {
    // Dark colors -> lighten while decreasing saturation slightly
    if (hsl.l < 0.4) {
      const newLightness = 0.65 + 0.25 * hsl.l;
      const newSaturation = Math.max(hsl.s * 0.9, 0.1);
      return tinycolor({
        h: hsl.h,
        s: newSaturation,
        l: newLightness,
      }).toHexString();
    }
    // Light colors -> darken
    if (hsl.l > 0.6) {
      const newLightness = 0.15 + 0.25 * (1 - hsl.l);
      return tinycolor({
        h: hsl.h,
        s: hsl.s * 1.1,
        l: newLightness,
      }).toHexString();
    }
    // Mid-range colors -> adjust based on saturation
    const targetLightness = hsl.s > 0.5 ? 0.7 : 0.3;
    return tinycolor({
      h: hsl.h,
      s: hsl.s,
      l: targetLightness,
    }).toHexString();
  }

  // Simple inversion (not recommended for most cases)
  return tinycolor({ h: hsl.h, s: hsl.s, l: 1 - hsl.l }).toHexString();
}

/**
 * Generates light mode colors for common semantic color names
 */
export function generateSemanticLightModeColor(
  colorName: string,
  darkValue: string,
): string {
  const lowerName = colorName.toLowerCase();

  // Semantic color mappings for light mode (inverse of dark mode)
  const semanticMappings: Record<
    string,
    (color: tinycolor.Instance) => string
  > = {
    background: (color) => {
      const hsl = color.toHsl();
      // Dark backgrounds -> very light
      if (hsl.l < 0.1) {
        return "#fafafa";
      }
      return generateLightModeColor(darkValue);
    },
    foreground: (color) => {
      const hsl = color.toHsl();
      // Light text -> dark text
      if (hsl.l > 0.8) {
        return "#0a0a0a";
      }
      return generateLightModeColor(darkValue);
    },
    border: (color) => {
      const hsl = color.toHsl();
      // Dark borders -> subtle light borders
      if (hsl.l < 0.2) {
        return tinycolor({ h: hsl.h, s: hsl.s * 0.5, l: 0.8 }).toHexString();
      }
      return generateLightModeColor(darkValue);
    },
    primary: (color) => {
      // Primary colors should remain vibrant but adjusted for light backgrounds
      const hsl = color.toHsl();
      return tinycolor({
        h: hsl.h,
        s: Math.min(hsl.s * 1.1, 1),
        l: Math.max(Math.min(hsl.l, 0.7), 0.4),
      }).toHexString();
    },
    secondary: (color) => {
      // Secondary colors - slightly muted compared to primary
      const hsl = color.toHsl();
      return tinycolor({
        h: hsl.h,
        s: hsl.s * 0.8,
        l: Math.max(Math.min(hsl.l, 0.6), 0.4),
      }).toHexString();
    },
    muted: (color) => {
      // Muted colors should remain subtle
      const hsl = color.toHsl();
      return tinycolor({
        h: hsl.h,
        s: hsl.s * 0.7,
        l: 0.85,
      }).toHexString();
    },
    accent: (color) => {
      // Accent colors should pop on light backgrounds
      const hsl = color.toHsl();
      return tinycolor({
        h: hsl.h,
        s: Math.min(hsl.s * 1.2, 1),
        l: 0.4,
      }).toHexString();
    },
  };

  // Check for semantic patterns
  for (const [pattern, generator] of Object.entries(semanticMappings)) {
    if (lowerName.includes(pattern)) {
      const color = tinycolor(darkValue);
      if (color.isValid()) {
        return generator(color);
      }
    }
  }

  // Default generation
  return generateLightModeColor(darkValue);
}

/**
 * Generates dark mode colors for common semantic color names
 */
export function generateSemanticDarkModeColor(
  colorName: string,
  lightValue: string,
): string {
  const lowerName = colorName.toLowerCase();

  // Semantic color mappings
  const semanticMappings: Record<
    string,
    (color: tinycolor.Instance) => string
  > = {
    background: (color) => {
      const hsl = color.toHsl();
      // Light backgrounds -> very dark
      if (hsl.l > 0.9) {
        return "#0a0a0a";
      }
      return generateDarkModeColor(lightValue);
    },
    foreground: (color) => {
      const hsl = color.toHsl();
      // Dark text -> light text
      if (hsl.l < 0.2) {
        return "#fafafa";
      }
      return generateDarkModeColor(lightValue);
    },
    border: (color) => {
      const hsl = color.toHsl();
      // Light borders -> subtle dark borders
      if (hsl.l > 0.8) {
        return tinycolor({ h: hsl.h, s: hsl.s * 0.5, l: 0.2 }).toHexString();
      }
      return generateDarkModeColor(lightValue);
    },
    primary: (color) => {
      // Primary colors should remain vibrant but adjusted for dark backgrounds
      const hsl = color.toHsl();
      return tinycolor({
        h: hsl.h,
        s: Math.min(hsl.s * 1.1, 1),
        l: Math.max(Math.min(hsl.l, 0.6), 0.4),
      }).toHexString();
    },
    secondary: (color) => {
      // Secondary colors - slightly muted compared to primary
      const hsl = color.toHsl();
      return tinycolor({
        h: hsl.h,
        s: hsl.s * 0.8,
        l: Math.max(Math.min(hsl.l, 0.5), 0.3),
      }).toHexString();
    },
    muted: (color) => {
      // Muted colors should remain subtle
      const hsl = color.toHsl();
      return tinycolor({
        h: hsl.h,
        s: hsl.s * 0.7,
        l: 0.15,
      }).toHexString();
    },
    accent: (color) => {
      // Accent colors should pop on dark backgrounds
      const hsl = color.toHsl();
      return tinycolor({
        h: hsl.h,
        s: Math.min(hsl.s * 1.2, 1),
        l: 0.5,
      }).toHexString();
    },
  };

  // Check for semantic patterns
  for (const [pattern, generator] of Object.entries(semanticMappings)) {
    if (lowerName.includes(pattern)) {
      const color = tinycolor(lightValue);
      if (color.isValid()) {
        return generator(color);
      }
    }
  }

  // Default generation
  return generateDarkModeColor(lightValue);
}

/**
 * Checks if a color would have sufficient contrast against a dark background
 */
export function hasGoodDarkModeContrast(
  color: string,
  backgroundColor = "#0a0a0a",
): boolean {
  const fg = tinycolor(color);
  const bg = tinycolor(backgroundColor);

  if (!fg.isValid() || !bg.isValid()) {
    return false;
  }

  const contrast = tinycolor.readability(fg, bg);
  return contrast >= 4.5; // WCAG AA standard
}

/**
 * Generates a complete dark mode color palette from light mode colors
 * Returns colors in nested structure under 'dark' key
 */
export function generateDarkModePalette(
  lightColors: Record<string, string | Record<string, string>>,
): { dark: Record<string, string | Record<string, string>> } {
  const darkNestedColors: Record<string, string | Record<string, string>> = {};

  for (const [key, value] of Object.entries(lightColors)) {
    if (typeof value === "string") {
      // Single color value
      darkNestedColors[key] = generateSemanticDarkModeColor(key, value);
    } else if (typeof value === "object" && value !== null) {
      // Color scale (e.g., gray-50, gray-100, etc.)
      const darkScale: Record<string, string> = {};

      for (const [shade, shadeValue] of Object.entries(value)) {
        if (typeof shadeValue === "string") {
          // For number-based shades, invert the scale
          const shadeNum = Number.parseInt(shade);
          if (!Number.isNaN(shadeNum)) {
            // Invert numeric shades (50 -> 950, 100 -> 900, etc.)
            const invertedShade =
              shade === "DEFAULT" ? "DEFAULT" : String(1000 - shadeNum);
            darkScale[shade] = generateSemanticDarkModeColor(
              `${key}-${invertedShade}`,
              shadeValue,
            );
          } else {
            // Non-numeric shades (like DEFAULT)
            darkScale[shade] = generateSemanticDarkModeColor(
              `${key}-${shade}`,
              shadeValue,
            );
          }
        }
      }

      darkNestedColors[key] = darkScale;
    }
  }

  // Return colors nested under 'dark' key
  return {
    dark: darkNestedColors,
  };
}
